import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../Utils/persistHelpers"
import { logout } from "../../LoginPage/redux/auth.actions"
import { MARKETING_SLICE_NAME } from "./marketing.constants"
import {
  createCampaign,
  createSource,
  generateLink,
  getCampaign,
  getCampaignData,
  getLinkUsers,
  getMarketingLinks,
  getSource,
  getSourceData,
  getTotalUsers
} from "./marketing.thunks"

/* ------------- Initial State ------------- */

const emptyPage = {
  count: 0,
  next: null,
  previous: null,
  results: []
}

const initialState = {
  fetching: false,
  result: { ...emptyPage },

  fetchingSource: false,
  source: { ...emptyPage },

  fetchingCampaign: false,
  campaign: { ...emptyPage },

  options: {
    source: [],
    campaign: []
  },

  fetchingUsers: false,
  users: { ...emptyPage },

  filtersLastUpd: null,
  filters: {
    period: "27 days"
  },
  getTotalUsersFetching: false,
  totalUsersData: null,

  saveFetching: false,
  saveResult: null,

  sourceData: null,
  campaignData: null
}

// Every request except get source / get campaign / get link users routed its failure through
// the legacy shared `marketingFailure` handler, which cleared those three spinners — kept as-is.
const marketingFailure = (state) => {
  state.fetching = false
  state.saveFetching = false
  state.getTotalUsersFetching = false
}

const toOptions = (page) =>
  page?.results?.map((item) => ({ value: item.id, label: item.name })) || []

const slice = createSlice({
  name: MARKETING_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments — `marketingSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `marketingSetProp({ key, value })`,
    // matching the `balanceSetProp`/`creditsSetProp` slices.
    marketingSetProp(state, action) {
      const { key, value } = action.payload
      if (key === "filters") {
        state.filtersLastUpd = new Date().getTime()
        state.filters = { ...state.filters, ...value }
      } else {
        state[key] = value
      }
    },
    resetSaveResult(state) {
      state.saveResult = null
    },
    resetLinkUsers(state) {
      state.users = { ...emptyPage }
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- marketing links ---------------- */
      .addCase(getMarketingLinks.pending, (state) => {
        state.fetching = true
      })
      .addCase(getMarketingLinks.fulfilled, (state, action) => {
        state.fetching = false
        state.result = action.payload
      })
      .addCase(getMarketingLinks.rejected, marketingFailure)

      /* ---------------- source chart ---------------- */
      .addCase(getSourceData.pending, (state) => {
        state.fetching = true
      })
      .addCase(getSourceData.fulfilled, (state, action) => {
        state.fetching = false
        state.sourceData = action.payload
      })
      .addCase(getSourceData.rejected, marketingFailure)

      /* ---------------- campaign chart ---------------- */
      .addCase(getCampaignData.pending, (state) => {
        state.fetching = true
      })
      .addCase(getCampaignData.fulfilled, (state, action) => {
        state.fetching = false
        state.campaignData = action.payload
      })
      .addCase(getCampaignData.rejected, marketingFailure)

      /* ---------------- total users chart ---------------- */
      .addCase(getTotalUsers.pending, (state) => {
        state.getTotalUsersFetching = true
      })
      .addCase(getTotalUsers.fulfilled, (state, action) => {
        state.getTotalUsersFetching = false
        state.totalUsersData = action.payload
      })
      .addCase(getTotalUsers.rejected, marketingFailure)

      /* ---------------- source list ---------------- */
      .addCase(getSource.pending, (state) => {
        state.fetchingSource = true
      })
      .addCase(getSource.fulfilled, (state, action) => {
        state.fetchingSource = false
        state.source = action.payload
        state.options.source = toOptions(action.payload)
      })
      .addCase(getSource.rejected, (state) => {
        state.fetchingSource = false
      })

      /* ---------------- campaign list ---------------- */
      .addCase(getCampaign.pending, (state) => {
        state.fetchingCampaign = true
      })
      .addCase(getCampaign.fulfilled, (state, action) => {
        state.fetchingCampaign = false
        state.campaign = action.payload
        state.options.campaign = toOptions(action.payload)
      })
      .addCase(getCampaign.rejected, (state) => {
        state.fetchingCampaign = false
      })

      /* ---------------- create source ---------------- */
      .addCase(createSource.pending, (state) => {
        state.saveFetching = true
      })
      // The legacy success handlers ignored the created entity and only raised a notification
      // flag; the refreshed list arrives through the `getSource` thunk the create dispatches.
      .addCase(createSource.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { type: "source_add", message: "Source has been successfully created." }
      })
      .addCase(createSource.rejected, marketingFailure)

      /* ---------------- create campaign ---------------- */
      .addCase(createCampaign.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createCampaign.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = {
          type: "campaign_add",
          message: "Campaign has been successfully created."
        }
      })
      .addCase(createCampaign.rejected, marketingFailure)

      /* ---------------- generate link ---------------- */
      .addCase(generateLink.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(generateLink.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { type: "generate_link", message: "Link has been successfully created." }
      })
      .addCase(generateLink.rejected, marketingFailure)

      /* ---------------- link users ---------------- */
      .addCase(getLinkUsers.pending, (state) => {
        state.fetchingUsers = true
      })
      .addCase(getLinkUsers.fulfilled, (state, action) => {
        state.fetchingUsers = false
        state.users = action.payload
      })
      .addCase(getLinkUsers.rejected, (state) => {
        state.fetchingUsers = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, () => initialState)
  }
})

export const marketingActions = slice.actions

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: MARKETING_SLICE_NAME,
    storage,
    // Only the period picker is worth restoring — every table and chart is re-fetched on mount.
    whitelist: ["filters", "filtersLastUpd"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
